import { useState, useCallback, useEffect } from 'react'
import { useAccount } from './useAccount'
import { useDagProgram } from './useDagProgram'
import { initializeLightClient } from '../programs/lightClient'
import { PublicKey } from '@solana/web3.js'

interface UserWorkVote {
  voteDirection: 'for' | 'against' | null
  votePower: number
  timestamp: number
}

export function useUserWorkVote({ 
  workItemPda, 
  proposalPda, 
  projectPda 
}: { 
  workItemPda: string
  proposalPda: string
  projectPda: PublicKey 
}) {
  const [userWorkVote, setUserWorkVote] = useState<UserWorkVote | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const { account } = useAccount()
  const { program } = useDagProgram()

  const fetchUserWorkVote = useCallback(async () => {
    if (!account?.address || !program || !workItemPda || !proposalPda) {
      setUserWorkVote(null)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const walletPubkey = new PublicKey(account.address)
      const proposalPdaKey = new PublicKey(proposalPda)
      const workItemPdaKey = new PublicKey(workItemPda)

      // Initialize Light Protocol client
      const lightClient = initializeLightClient(program)

      // Derive the work vote address (same pattern as in voting)
      const voteAddress = await lightClient.deriveAddressWithSeed([
        Buffer.from("work_vote"),
        walletPubkey.toBuffer(),
        proposalPdaKey.toBuffer(),
        workItemPdaKey.toBuffer(),
      ])

      // Try to fetch the compressed vote account - handle errors gracefully
      try {
        const voteAccount = await lightClient.rpc.getCompressedAccount(
          lightClient.bufferToBN254Scalar(voteAddress.address.toBuffer())
        )

        if (voteAccount && voteAccount.data) {
          // Parse the vote data using the program's coder
          const voteData = program.coder.types.decode('workItemVote', voteAccount.data.data)
          
          setUserWorkVote({
            voteDirection: voteData.approve ? 'for' : 'against',
            votePower: voteData.votePower.toNumber(),
            timestamp: voteData.timestamp.toNumber()
          })
        } else {
          // No vote found
          setUserWorkVote(null)
        }
      } catch (compressedAccountError) {
        // This is expected if the user hasn't voted yet - the account doesn't exist
        console.log("No compressed work vote account found (user likely hasn't voted yet):", compressedAccountError)
        setUserWorkVote(null)
        // Don't set this as an error since it's expected behavior
      }
    } catch (err) {
      console.error('Failed to fetch user work vote:', err)
      // If account doesn't exist, user hasn't voted
      setUserWorkVote(null)
      // Only set error for unexpected issues, not for missing vote accounts
      if (err instanceof Error && !err.message.includes('compressed account')) {
        setError(err)
      } else {
        setError(null) // Don't show error for non-existent vote
      }
    } finally {
      setIsLoading(false)
    }
  }, [account?.address, program, workItemPda, proposalPda])

  useEffect(() => {
    fetchUserWorkVote()
  }, [fetchUserWorkVote])

  return {
    userWorkVote,
    isLoading,
    error,
    refetchUserWorkVote: fetchUserWorkVote,
  }
} 